/**
 * announcement-bar.js
 * Drives the promo slider in the header announcement bar
 * (snippets/announcement-carousel.liquid).
 *
 *   - Stacked slides shown one at a time; arrows / swipe / ← → cycle and wrap.
 *   - Optional auto-cycle, paused on hover, on keyboard focus, and while the
 *     tab is hidden; skipped entirely under prefers-reduced-motion.
 *   - When the active message is wider than the bar it marquee-scrolls, unless
 *     the merchant switched that off. Re-measured on resize and once web fonts
 *     have loaded, since both change the measured text width.
 *
 * Extracted from sections/header.liquid's {% javascript %} block. Logic is
 * unchanged apart from teardown: every listener is now bound through a
 * per-root AbortController, so re-initialising after `shopify:section:load`
 * replaces its listeners instead of stacking a fresh set on window/document
 * every time the merchant saves in the theme editor.
 *
 * Contract: [data-announcement] root, with [data-announcement-slide],
 * [data-announcement-track], [data-announcement-prev|next] inside, and the
 * marquee / autoplay / interval attributes on the root.
 */
(function () {
  'use strict';

  var MARQUEE_SPEED = 40; // px per second

  function init(root) {
    // Replace any previous binding for this element rather than adding to it.
    if (root.__announcementAbort) root.__announcementAbort.abort();
    var ac = (root.__announcementAbort = new AbortController());
    var signal = ac.signal;

    var slides = Array.prototype.slice.call(root.querySelectorAll('[data-announcement-slide]'));
    if (!slides.length) return;

    var marqueeOn = root.getAttribute('data-announcement-marquee') !== 'false';

    // Toggle the marquee on a slide based on whether its text overflows.
    var updateMarquee = function (slide) {
      var content = slide && slide.querySelector('.announcement-carousel__content');
      if (!content) return;
      slide.classList.remove('is-marquee');
      if (!marqueeOn) return;
      var overflow = content.scrollWidth - content.clientWidth;
      if (overflow > 1) {
        content.style.setProperty('--marquee-shift', -overflow + 'px');
        content.style.setProperty('--marquee-duration', Math.max(5, overflow / MARQUEE_SPEED).toFixed(2) + 's');
        slide.classList.add('is-marquee');
      } else {
        content.style.removeProperty('--marquee-shift');
        content.style.removeProperty('--marquee-duration');
      }
    };

    var index = Math.max(
      0,
      slides.findIndex(function (s) {
        return s.classList.contains('is-active');
      })
    );

    if (slides.length > 1) {
      var prevBtn = root.querySelector('[data-announcement-prev]');
      var nextBtn = root.querySelector('[data-announcement-next]');
      var track = root.querySelector('[data-announcement-track]');

      var show = function (n) {
        slides[index]?.classList.remove('is-marquee');
        index = (n + slides.length) % slides.length;
        slides.forEach(function (slide, i) {
          slide.classList.toggle('is-active', i === index);
        });
        updateMarquee(slides[index]);
      };

      var autoplayOn = root.getAttribute('data-announcement-autoplay') === 'true';
      var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      var interval = parseInt(root.getAttribute('data-announcement-interval'), 10);
      if (!interval || interval < 1000) interval = 5000;
      var timer = null;
      var paused = false;

      var stopAuto = function () {
        if (timer) {
          clearInterval(timer);
          timer = null;
        }
      };
      var startAuto = function () {
        stopAuto();
        if (!autoplayOn || reduceMotion || paused || document.hidden) return;
        timer = setInterval(function () {
          show(index + 1);
        }, interval);
      };

      // Stop the timer when this root is torn down, or it survives the element.
      signal.addEventListener('abort', stopAuto);

      prevBtn?.addEventListener(
        'click',
        function () {
          show(index - 1);
          startAuto();
        },
        { signal: signal }
      );
      nextBtn?.addEventListener(
        'click',
        function () {
          show(index + 1);
          startAuto();
        },
        { signal: signal }
      );

      root.addEventListener(
        'keydown',
        function (event) {
          if (event.key === 'ArrowLeft') {
            event.preventDefault();
            show(index - 1);
            startAuto();
          } else if (event.key === 'ArrowRight') {
            event.preventDefault();
            show(index + 1);
            startAuto();
          }
        },
        { signal: signal }
      );

      // Lightweight swipe — a horizontal drag past the threshold steps once.
      // A plain link click (negligible dx) falls through and navigates normally.
      var startX = null;
      track?.addEventListener(
        'pointerdown',
        function (event) {
          startX = event.clientX;
        },
        { signal: signal }
      );
      track?.addEventListener(
        'pointerup',
        function (event) {
          if (startX === null) return;
          var dx = event.clientX - startX;
          startX = null;
          if (Math.abs(dx) > 30) {
            show(dx < 0 ? index + 1 : index - 1);
            startAuto();
          }
        },
        { signal: signal }
      );

      // Pause on hover / keyboard focus within the bar; resume on leave / blur.
      root.addEventListener('mouseenter', function () { paused = true; stopAuto(); }, { signal: signal });
      root.addEventListener('mouseleave', function () { paused = false; startAuto(); }, { signal: signal });
      root.addEventListener('focusin', function () { paused = true; stopAuto(); }, { signal: signal });
      root.addEventListener('focusout', function () { paused = false; startAuto(); }, { signal: signal });
      document.addEventListener(
        'visibilitychange',
        function () {
          if (document.hidden) stopAuto();
          else startAuto();
        },
        { signal: signal }
      );

      startAuto();
    }

    // Initial + responsive overflow check on the active slide. Re-check once
    // web fonts finish loading, since they change the measured text width.
    updateMarquee(slides[index]);
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function () {
        if (!signal.aborted) updateMarquee(slides[index]);
      });
    }
    var resizeTimer = null;
    window.addEventListener(
      'resize',
      function () {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(function () {
          updateMarquee(slides[index]);
        }, 150);
      },
      { signal: signal }
    );
    signal.addEventListener('abort', function () {
      clearTimeout(resizeTimer);
    });
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-announcement]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
